import { redactReplayEvent, redactString, redactUiBreadcrumbMessage } from './sentryRedaction';

/**
 * Replay `beforeAddRecordingEvent` hook (SEE-1834).
 *
 * The SDK only passes **custom** events (`type: 5`) through this hook: breadcrumbs and performance
 * spans. Full snapshots, DOM mutations and the meta `href` never arrive here, so DOM privacy is
 * the job of the selectors in `sentryReplayPrivacy.ts`, not of this function.
 *
 * Returning the event is mandatory: a hook that returns nothing drops every custom event.
 */

const REDACTED = '[Redacted]';

/** Breadcrumbs that carry `data.url` (`window.location.href` at the time). */
const URL_BREADCRUMB_CATEGORIES = ['ui.slowClickDetected', 'ui.multiClick', 'replay.hydrate-error'];

interface ReplayCustomEventPayload {
  category?: string;
  message?: string;
  op?: string;
  description?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

interface ReplayCustomEvent {
  type?: number;
  data?: { tag?: string; payload?: ReplayCustomEventPayload; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * Run one scrub. If it throws, fall back to redacting the field it was scrubbing, so a failure
 * removes data instead of uploading it, and never take the rest of the event down with it.
 */
function guarded(scrub: () => void, redactOnFailure: () => void): void {
  try {
    scrub();
  } catch {
    try {
      redactOnFailure();
    } catch {
      // The field can't be written either; leave the event as it is rather than drop it.
    }
  }
}

function scrubBreadcrumb(payload: ReplayCustomEventPayload): void {
  const category = typeof payload.category === 'string' ? payload.category : '';
  if (category.startsWith('ui.')) {
    // `message` is built from the live DOM and copies aria-label/title/alt, unmasked.
    guarded(
      () => {
        if (typeof payload.message === 'string') payload.message = redactUiBreadcrumbMessage(payload.message);
      },
      () => {
        payload.message = REDACTED;
      }
    );
  }
  if (URL_BREADCRUMB_CATEGORIES.includes(category)) {
    guarded(
      () => {
        const url = payload.data?.url;
        if (payload.data && typeof url === 'string') payload.data.url = redactString(url);
      },
      () => {
        if (payload.data) payload.data.url = REDACTED;
      }
    );
  }
}

function scrubNavigationSpan(payload: ReplayCustomEventPayload): void {
  if (typeof payload.op !== 'string' || !payload.op.startsWith('navigation.')) return;
  guarded(
    () => {
      if (typeof payload.description === 'string') payload.description = redactString(payload.description);
    },
    () => {
      payload.description = REDACTED;
    }
  );
  guarded(
    () => {
      const previous = payload.data?.previous;
      if (payload.data && typeof previous === 'string') payload.data.previous = redactString(previous);
    },
    () => {
      if (payload.data) payload.data.previous = REDACTED;
    }
  );
}

export function scrubReplayRecordingEvent<T extends ReplayCustomEvent>(event: T): T {
  // SEE-1087 network-span redaction first. Not guarded: if it throws, the SDK drops this one event,
  // which fails closed — an unredacted request body must not upload.
  redactReplayEvent(event);
  const payload = event.data?.payload;
  if (payload && typeof payload === 'object') {
    if (event.data?.tag === 'breadcrumb') scrubBreadcrumb(payload);
    if (event.data?.tag === 'performanceSpan') scrubNavigationSpan(payload);
  }
  return event;
}
